import { Insights } from '@mui/icons-material';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import Title from 'lib/designSystem/Title';
import { formatMoney, validateProducts } from 'lib/functions/utils';
import React from 'react';
import { useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { OperativeProfileData } from '../index';

const LabelValueRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
    >
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
};

const InsightsSummary = ({ data }: { data: OperativeProfileData }) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  // const showSgr = validateProducts(user, ['sgr']);
  const showCredit = validateProducts(user, ['credit']);
  const showConfirming = validateProducts(user, ['confirming']);
  const showFactoring = validateProducts(user, ['factoring']);
  const factoringInsights = data.insights.factoring;
  const confirmingInsights = data.insights.confirming;
  const creditInsights = data.insights.credit;
  const extensionsInsights = data.extensions;

  return (
    <>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
        }}
      >
        <Title
          title="Insights"
          icon={<Insights color="primary" sx={{ fontSize: 20 }} />}
          color="primary"
        />

        <Grid container spacing={3}>
          {showFactoring && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Factoring
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Documentos
                  </Typography>
                  <LabelValueRow
                    label="Comprados históricos"
                    value={factoringInsights.factoringBoughtDocumentsQuantity}
                  />
                  <LabelValueRow
                    label="Pagados"
                    value={factoringInsights.factoringPaidDocumentsQuantity}
                  />
                  <LabelValueRow
                    label="Vigentes en fecha"
                    value={
                      factoringInsights.factoringActiveDocumentsOnDateQuantity
                    }
                  />
                  <LabelValueRow
                    label="Vigentes en mora"
                    value={
                      factoringInsights.factoringActiveDocumentsInArrearsQuantity
                    }
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Deudores
                  </Typography>
                  <LabelValueRow
                    label="Deudores históricos"
                    value={
                      factoringInsights.factoringHistoricStakeholdersQuantity
                    }
                  />
                  <LabelValueRow
                    label="Deudores activos"
                    value={
                      factoringInsights.factoringActiveStakeholdersQuantity
                    }
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Promedios
                  </Typography>
                  <LabelValueRow
                    label="Promedio días de curse de operación"
                    value={factoringInsights.factoringAverageCurseDays}
                  />
                  <LabelValueRow
                    label="Promedio tasa de curse de operación"
                    value={factoringInsights.factoringAverageCurseRate}
                  />
                  <LabelValueRow
                    label="Promedio de pago (días desde F.E.)"
                    value={factoringInsights.factoringAveragePaymentDays}
                  />
                  <LabelValueRow
                    label="Promedio monto documento"
                    value={formatMoney(
                      factoringInsights.factoringAverageBoughtDocumentAmount,
                    )}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Montos de documentos (sin mora)
                  </Typography>
                  <LabelValueRow
                    label="Monto documentos comprados"
                    value={formatMoney(
                      factoringInsights.factoringBoughtDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos pagados"
                    value={formatMoney(
                      factoringInsights.factoringPaidDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en fecha"
                    value={formatMoney(
                      factoringInsights.factoringOnDateDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en mora"
                    value={formatMoney(
                      factoringInsights.factoringInArrearsDocumentAmount,
                    )}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Recaudaciones
                  </Typography>
                  <LabelValueRow
                    label="Por cliente"
                    value={formatMoney(
                      factoringInsights.factoringConciliatedByBusinessAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Por deudor"
                    value={formatMoney(
                      factoringInsights.factoringConciliatedByStakeholderAmount,
                    )}
                  />
                </Box>
              </Box>
            </Grid>
          )}

          {showConfirming && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Confirming
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Documentos
                  </Typography>
                  <LabelValueRow
                    label="Comprados históricos"
                    value={confirmingInsights.confirmingBoughtDocumentsQuantity}
                  />
                  <LabelValueRow
                    label="Pagados"
                    value={confirmingInsights.confirmingPaidDocumentsQuantity}
                  />
                  <LabelValueRow
                    label="Vigentes en fecha"
                    value={
                      confirmingInsights.confirmingActiveDocumentsOnDateQuantity
                    }
                  />
                  <LabelValueRow
                    label="Vigentes en mora"
                    value={
                      confirmingInsights.confirmingActiveDocumentsInArrearsQuantity
                    }
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Proveedores
                  </Typography>
                  <LabelValueRow
                    label="Proveedores históricos"
                    value={
                      confirmingInsights.confirmingHistoricBusinessesQuantity
                    }
                  />
                  <LabelValueRow
                    label="Proveedores activos"
                    value={
                      confirmingInsights.confirmingActiveBusinessesQuantity
                    }
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Promedios
                  </Typography>
                  <LabelValueRow
                    label="Promedio días de curse de operación"
                    value={confirmingInsights.confirmingAverageCurseDays}
                  />
                  <LabelValueRow
                    label="Promedio tasa de curse de operación"
                    value={confirmingInsights.confirmingAverageCurseRate}
                  />
                  <LabelValueRow
                    label="Promedio de pago (días desde F.E.)"
                    value={confirmingInsights.confirmingAveragePaymentDays}
                  />
                  <LabelValueRow
                    label="Promedio monto documento"
                    value={formatMoney(
                      confirmingInsights.confirmingAverageBoughtDocumentAmount,
                    )}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Montos de documentos (sin mora)
                  </Typography>
                  <LabelValueRow
                    label="Monto documentos comprados"
                    value={formatMoney(
                      confirmingInsights.confirmingBoughtDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos pagados"
                    value={formatMoney(
                      confirmingInsights.confirmingPaidDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en fecha"
                    value={formatMoney(
                      confirmingInsights.confirmingOnDateDocumentAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en mora"
                    value={formatMoney(
                      confirmingInsights.confirmingInArrearsDocumentAmount,
                    )}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Recaudaciones
                  </Typography>
                  <LabelValueRow
                    label="Por proveedor"
                    value={formatMoney(
                      confirmingInsights.confirmingConciliatedByBusinessAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Por cliente-deudor"
                    value={formatMoney(
                      confirmingInsights.confirmingConciliatedByStakeholderAmount,
                    )}
                  />
                </Box>
              </Box>
            </Grid>
          )}

          {showCredit && (
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  gutterBottom
                >
                  Créditos
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Documentos
                  </Typography>
                  <LabelValueRow
                    label="Pagados"
                    value={creditInsights.creditPaidDocumentsQuantity}
                  />
                  <LabelValueRow
                    label="Vigentes en fecha"
                    value={creditInsights.creditActiveDocumentsOnDateQuantity}
                  />
                  <LabelValueRow
                    label="Vigentes en mora"
                    value={
                      creditInsights.creditActiveDocumentsInArrearsQuantity
                    }
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Promedios
                  </Typography>
                  <LabelValueRow
                    label="Promedio monto documento"
                    value={formatMoney(
                      creditInsights.creditAverageDocumentAmount,
                    )}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    marginTop={2}
                    gutterBottom
                  >
                    Montos de documentos (sin mora)
                  </Typography>
                  <LabelValueRow
                    label="Monto documentos pagados"
                    value={formatMoney(
                      creditInsights.creditPaidDocumentsAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en fecha"
                    value={formatMoney(
                      creditInsights.creditActiveDocumentsOnDateAmount,
                    )}
                  />
                  <LabelValueRow
                    label="Monto documentos en mora"
                    value={formatMoney(
                      creditInsights.creditActiveDocumentsInArrearsAmount,
                    )}
                  />
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
                gutterBottom
              >
                Prórrogas
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Activas
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={extensionsInsights.active.quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(extensionsInsights.active.amount)}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  marginTop={2}
                  gutterBottom
                >
                  Completadas
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={extensionsInsights.completed.quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(extensionsInsights.completed.amount)}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
                gutterBottom
              >
                Cartera Vigente
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Factura Electrónica
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={data.activeMoneyByType['electronic-invoice'].quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(
                    data.activeMoneyByType['electronic-invoice'].amount,
                  )}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  marginTop={2}
                  gutterBottom
                >
                  Factura con flujo de cheques
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={data.activeMoneyByType['check-invoice'].quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(
                    data.activeMoneyByType['check-invoice'].amount,
                  )}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  marginTop={2}
                  gutterBottom
                >
                  Orden de compra
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={data.activeMoneyByType['buy-order'].quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(
                    data.activeMoneyByType['buy-order'].amount,
                  )}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  marginTop={2}
                  gutterBottom
                >
                  Total
                </Typography>
                <LabelValueRow
                  label="Documentos"
                  value={data.activeMoneyByType.total.quantity}
                />
                <LabelValueRow
                  label="Monto documentos"
                  value={formatMoney(data.activeMoneyByType.total.amount)}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default InsightsSummary;
